import type {
  Contest,
  ContestMeasure,
  ContestRewardType,
  ContestStatus,
} from "@/types/contests";

export const MEASURE_LABELS: Record<ContestMeasure, string> = {
  Premium: "Składka (przypis)",
  PolicyCount: "Liczba polis",
  Commission: "Wypracowana prowizja",
  NewClients: "Nowi klienci",
};

/** How each measure is computed — shown in the form and to the agents. */
export const MEASURE_HINTS: Record<ContestMeasure, string> = {
  Premium:
    "Suma przypisu z importów. Polisa dzielona liczy się proporcjonalnie, storno pomniejsza wynik.",
  PolicyCount:
    "Liczba polis z dodatnią składką. Polisa dzielona liczy się raz — agentowi głównemu.",
  Commission:
    "Prowizja z importów, jaką polisy przyniosły agencji (przed podziałem z agentem).",
  NewClients:
    "Klienci dodani przez agenta w module „Klienci i RODO”. Nie da się zawęzić do towarzystw ani produktów.",
};

export const REWARD_TYPE_LABELS: Record<ContestRewardType, string> = {
  TopPlaces: "Miejsca 1–N",
  AboveThreshold: "Wszyscy powyżej progu",
  Tiers: "Progi stopniowane",
};

export const STATUS_LABELS: Record<ContestStatus, string> = {
  Draft: "Szkic",
  Active: "Aktywny",
  Finished: "Zakończony",
};

export const STATUS_VARIANTS = {
  Draft: "default",
  Active: "success",
  Finished: "info",
} as const satisfies Record<ContestStatus, string>;

export function isMoney(measure: ContestMeasure) {
  return measure === "Premium" || measure === "Commission";
}

export function formatValue(measure: ContestMeasure, value: number): string {
  return isMoney(measure)
    ? value.toLocaleString("pl-PL", { style: "currency", currency: "PLN" })
    : value.toLocaleString("pl-PL", { maximumFractionDigits: 0 });
}

function formatDay(date: string) {
  return new Date(`${date}T00:00:00`).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatPeriod(contest: Pick<Contest, "startDate" | "endDate">) {
  return `${formatDay(contest.startDate)} – ${formatDay(contest.endDate)}`;
}

/** "Miejsca 1–3", "Od 20 polis", "Progi: 20 / 40 / 60". */
export function rewardSummary(contest: Contest): string {
  switch (contest.rewardType) {
    case "TopPlaces":
      return contest.topPlaces === 1
        ? "Nagroda za 1. miejsce"
        : `Nagrody za miejsca 1–${contest.topPlaces}`;
    case "AboveThreshold":
      return `Każdy od ${formatValue(contest.measure, contest.minimumThreshold ?? 0)}`;
    case "Tiers":
      return `Progi: ${contest.tiers.map((t) => formatValue(contest.measure, t.threshold)).join(" / ")}`;
  }
}

/** "Wszystkie TU, produkty: OC komunikacyjne" — what the contest counts. */
export function scopeSummary(contest: Contest): string {
  const insurers =
    contest.insurerNames.length === 0
      ? "wszystkie towarzystwa"
      : contest.insurerNames.join(", ");
  const products =
    contest.productNames.length === 0
      ? "wszystkie produkty"
      : `produkty: ${contest.productNames.join(", ")}`;
  return contest.measure === "NewClients"
    ? "klienci dodani w panelu"
    : `${insurers}; ${products}`;
}
